import type { LibrarySnapshot } from "@/types/library";

import { parseLibrarySnapshot, toIsoTimestamp } from "./snapshot";

/**
 * Per-user localStorage cache of the library, so pages can show it instantly
 * before the server answers. Keys include the user id, so one user's data is
 * never shown to another, and everything is wiped on sign-out.
 */

const KEY_PREFIX = "cage-mdb:library:v1:";
const VERSION = 1;

/** Keys written by older versions of the app (one global, not per-user, cache). */
const LEGACY_KEYS = ["user_data_cache"];

export interface CachedLibrary {
  library: LibrarySnapshot;
  /** Server time of the snapshot. */
  syncedAt: string;
}

export interface LibraryStorage {
  read(userId: string): CachedLibrary | null;
  write(userId: string, cached: CachedLibrary): void;
  /** Remove every cached library except this user's (and legacy data). */
  removeOthers(userId: string): void;
  /** Remove every cached library (sign-out). */
  clearAll(): void;
}

export const libraryStorageKey = (userId: string) => `${KEY_PREFIX}${userId}`;

/** True for keys this module (or older app versions) owns. */
export const isLibraryStorageKey = (key: string) =>
  key.startsWith(KEY_PREFIX) || LEGACY_KEYS.includes(key);

function defaultStorage(): Storage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Access can throw (e.g. storage disabled by the browser).
    return null;
  }
}

function ownedKeys(storage: Storage): string[] {
  const keys: string[] = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (key && isLibraryStorageKey(key)) keys.push(key);
  }

  return keys;
}

export function createLibraryStorage(
  storage: Storage | null = defaultStorage(),
): LibraryStorage {
  const remove = (keys: string[]) => {
    for (const key of keys) {
      try {
        storage?.removeItem(key);
      } catch {
        // Ignore: nothing else to do.
      }
    }
  };

  return {
    read(userId) {
      try {
        const raw = storage?.getItem(libraryStorageKey(userId));

        if (!raw) return null;
        const parsed = JSON.parse(raw) as Record<string, unknown>;
        const library = parseLibrarySnapshot(parsed.library);
        const syncedAt = toIsoTimestamp(parsed.syncedAt);

        if (parsed.v !== VERSION || parsed.userId !== userId) return null;
        if (!library || !syncedAt) return null;

        return { library, syncedAt };
      } catch {
        // Corrupt or unreadable: behave as if nothing was cached.
        return null;
      }
    },

    write(userId, cached) {
      try {
        storage?.setItem(
          libraryStorageKey(userId),
          JSON.stringify({ v: VERSION, userId, ...cached }),
        );
      } catch {
        // Quota exceeded or storage disabled: the cache is only an optimization.
      }
    },

    removeOthers(userId) {
      if (!storage) return;
      const keep = libraryStorageKey(userId);

      try {
        remove(ownedKeys(storage).filter((key) => key !== keep));
      } catch {
        // Ignore.
      }
    },

    clearAll() {
      if (!storage) return;
      try {
        remove(ownedKeys(storage));
      } catch {
        // Ignore.
      }
    },
  };
}
